import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setScrolled(window.scrollY > 60);
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const isAtTop = !scrolled;
  const navTextColor = isAtTop ? 'text-white' : 'text-foreground-700';
  const navHoverColor = isAtTop ? 'hover:text-white/80' : 'hover:text-foreground-950';
  const navBg = isAtTop
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70';
  const dropdownBg = isAtTop ? 'bg-background-50' : 'bg-background-50';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-10">
            <Link to="/" className="flex items-center gap-2.5 group">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${
                isAtTop ? 'bg-white/20 text-white' : 'bg-primary-500 text-background-50'
              }`}>
                <i className="ri-leaf-line"></i>
              </span>
              <span className={`text-[15px] font-semibold whitespace-nowrap transition-colors ${isAtTop ? 'text-white' : 'text-foreground-950'}`}>
                itaobuy.hair
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={`text-[14px] font-medium transition-colors whitespace-nowrap ${navTextColor} ${navHoverColor}`}
              >
                Home
              </Link>

              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1.5 text-[14px] font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextColor} ${navHoverColor}`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  Categories
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className={`absolute top-full left-0 mt-2 w-72 ${dropdownBg} rounded-2xl border border-background-200/70 py-3 max-h-[480px] overflow-y-auto shadow-lg`}>
                    <p className="px-4 pb-2 text-[10px] font-semibold uppercase tracking-wider text-foreground-400">Product Categories</p>
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2 text-[13px] text-foreground-700 hover:bg-secondary-50 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      >
                        <span className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </span>
                        {cat.name}
                      </a>
                    ))}
                    <div className="border-t border-background-200 mt-2 pt-2 px-4">
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 text-[12px] text-primary-600 hover:text-primary-700 font-medium transition-colors"
                      >
                        <i className="ri-external-link-line"></i>
                        View All on kaspreadsheet
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/itaobuy-spreadsheet-guide"
                className={`text-[14px] font-medium transition-colors whitespace-nowrap ${navTextColor} ${navHoverColor}`}
              >
                Shopping Guide
              </Link>

              <Link
                to="/itaobuy-beginner"
                className={`text-[14px] font-medium transition-colors whitespace-nowrap ${navTextColor} ${navHoverColor}`}
              >
                Guides
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`text-[14px] font-medium transition-colors whitespace-nowrap ${navTextColor} ${navHoverColor}`}
              >
                Safety
              </a>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 ${
                isAtTop
                  ? 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              itaobuy sheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isAtTop ? 'text-white hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-0.5 mb-4">
              <Link
                to="/"
                className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-4-line text-base text-primary-500 w-5"></i>
                Home
              </Link>
              <Link
                to="/itaobuy-spreadsheet-guide"
                className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-book-open-line text-base text-primary-500 w-5"></i>
                Shopping Guide
              </Link>
              <Link
                to="/itaobuy-beginner"
                className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-guide-line text-base text-primary-500 w-5"></i>
                Guides
              </Link>
            </div>

            <button
              className="w-full flex items-center justify-between px-3 py-2.5 text-[13px] font-semibold text-foreground-800 rounded-xl hover:bg-background-100 transition-colors mb-1 cursor-pointer"
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
            >
              <span className="flex items-center gap-2.5">
                <i className="ri-grid-line text-base text-primary-500 w-5"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-2 mb-3 bg-background-100 rounded-xl py-2 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-[12px] text-foreground-700 hover:bg-background-50 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 pt-3 mt-2 flex flex-col gap-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-2.5 text-[13px] font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
              >
                <i className="ri-shield-check-line text-base text-primary-500 w-5 mr-2.5"></i>
                Safety
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mt-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-[13px] font-semibold text-center hover:bg-primary-600 transition-colors"
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}